import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Vote, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

export default function MerchantVoting() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/merchant-suggestions").then((r) => setItems(r.data.suggestions || []));
  useEffect(() => { load(); }, []);

  const vote = async (merchantName) => {
    if (!user) { toast.info("Sign in to vote"); nav("/auth"); return; }
    setBusy(true);
    try {
      const r = await api.post("/merchant-suggestions/vote", { merchant_name: merchantName });
      toast.success(`Voted — ${r.data.votes} vote${r.data.votes === 1 ? "" : "s"}`);
      setName("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    } finally { setBusy(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    const cleaned = name.trim();
    if (cleaned.length < 2) return toast.error("Enter a merchant name");
    vote(cleaned);
  };

  return (
    <Card className="trust-card" data-testid="merchant-voting-card">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3">
          <Vote className="w-5 h-5 text-[#1E3A8A]" />
          <h2 className="text-xl font-semibold text-slate-900">Vote for the next playbook</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">Which merchant should we add next? Top vote-getters get built first.</p>

        <form onSubmit={submit} className="flex gap-2 mb-5">
          <Input
            data-testid="merchant-vote-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., LinkedIn Premium, Chegg, Blue Apron…"
            className="flex-1"
            maxLength={60}
          />
          <Button data-testid="merchant-vote-submit" type="submit" disabled={busy} className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]">
            Suggest & vote
          </Button>
        </form>

        {items.length > 0 && (
          <div className="space-y-2" data-testid="merchant-suggestions-list">
            {items.map((s) => (
              <div key={s.slug} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-200" data-testid={`merchant-suggestion-${s.slug}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{s.merchant_name}</p>
                  <p className="text-xs text-slate-500">{s.votes} vote{s.votes === 1 ? "" : "s"}</p>
                </div>
                <Button
                  onClick={() => vote(s.merchant_name)}
                  disabled={busy}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  data-testid={`vote-up-${s.slug}`}
                >
                  <ThumbsUp className="w-3.5 h-3.5 mr-1.5" /> Upvote
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
